import OCButton from '@cx-web/components/primitives/button';
import TextInput from '@cx-web/components/primitives/TextInput';
import { useCxRuntime } from '@cx-web/feature-cx-runtime';
import classnames from 'classnames';

import styles from './Onboarding.module.css';
import { useOnboarding } from './useOnboarding';

// NGF-6334: the framework-coupled input that used to be a prop (resourcesUrl) now arrives through the
// CX runtime adapter (useConfig). So the shared component's props are purely the CMS-driven config.
export type OnboardingProps = {
  hasMarginTop?: boolean;
};

// NGF-6334/NGF-6479: the single shared Onboarding view, consumed by both apps/atlas (RR7) and
// apps/atlas-astro (island). All framework seams are read through useCxRuntime() — labels via `t`
// (bare keys; the adapter handles the RESPONSIVE_ prefix), static assets via `resourcesUrl` — so this
// source is byte-identical across both apps. The CTA fires sign-up-container tracking and opens the
// MyOC auth modal (USER_SIGNUP); there is no form submission and only the secondary view exists.
export const Onboarding = ({ hasMarginTop }: OnboardingProps) => {
  const runtime = useCxRuntime();
  const t = runtime.useLabel();
  const { resourcesUrl } = runtime.useConfig();

  const { disabled, errorOnBlur, handleInputBlur, handleInputChange, handleSubmit, input, inputIsValid } =
    useOnboarding();

  // Renderers

  const renderBgImg = (): React.JSX.Element => {
    const bgUrl = `${resourcesUrl}/assets/img/onboarding/signup-nba.webp`;
    return (
      <img alt="onboarding-background" className={styles.bgImg} fetchPriority="high" loading="eager" src={bgUrl} />
    );
  };

  const renderTitle = (): React.JSX.Element => {
    return (
      <div className={styles.onboardingTitle}>
        <h2>{t('SIGN_UP_WITH_ODDSCHECKER')}</h2>
        <h2 className={styles.highlight}>{t('TO_BEAT_THE_ODDS')}</h2>
      </div>
    );
  };

  const renderDescription = (): React.JSX.Element => {
    return <div className={styles.description}>{t('SUBSCRIBE_TO_EMAIL')}</div>;
  };

  const renderTextInput = (): React.JSX.Element => {
    return (
      <TextInput
        data-testid="onboarding-email-input"
        hasError={errorOnBlur}
        inputClassName={classnames(styles.input, errorOnBlur ? styles.error : '')}
        isValid={inputIsValid}
        name="email"
        onBlur={handleInputBlur}
        onChange={({ target }) => handleInputChange(target.value)}
        placeholder={t('YOUR_EMAIL_ADDRESS')}
        shouldAutocomplete={false}
        type="email"
        value={input.value}
        wrapperClassName={styles.inputContainer}
      />
    );
  };

  const renderErrorBanner = (): React.JSX.Element | null => {
    if (!errorOnBlur) return null;

    return (
      <div className={styles.errorBanner} data-testid="error-banner">
        <div className={styles.errorTitle}>{t('INVALID_EMAIL')}</div>
        <div className={styles.errorMessage}>{t('EMAIL_ADDRESS_FORMAT')}</div>
      </div>
    );
  };

  const renderCTA = (): React.JSX.Element => {
    return (
      <div className={styles.ctaWrapper}>
        <OCButton
          className={styles.ctaBtn}
          disabled={disabled}
          onClick={handleSubmit}
          testId="onboarding-submit"
          type="secondary"
        >
          {t('SIGN_ME_UP')}
        </OCButton>
      </div>
    );
  };

  return (
    <div
      className={classnames(styles.wrapper, styles.secondary, hasMarginTop ? styles.marginTop : '')}
      data-testid="onboarding"
    >
      {renderBgImg()}
      <div className={styles.bgColor} />
      <div className={styles.contentWrapper}>
        {renderTitle()}
        <div className={styles.formContainer}>
          {renderDescription()}
          <div className={styles.inputWrapper}>
            {renderTextInput()}
            {renderCTA()}
          </div>
          {renderErrorBanner()}
        </div>
      </div>
    </div>
  );
};
